"use client";

import { useCustomer } from "@/hooks/useCustomer";
import { Locales } from "@/lib/i18n";
import { __, getPermalink } from "@/lib/i18n/functions";
import { cn, convertDate } from "@/lib/utils";
import { useCheckoutStore } from "@/stores/checkout-store";
import Link from "next/link";
import { usePathname } from "next/navigation";

type MyInformationProps = {
  locale: Locales;
};

const getTitleLabel = (title: string | undefined, locale: Locales) => {
  if (title === "mademoiselle") return __("Miss ", locale).trim();
  if (title === "madame") return __("Mrs ", locale).trim();
  if (title === "monsieur") return __("Mr", locale).trim();

  return "";
};

const MyInformation = ({ locale }: MyInformationProps) => {
  const { customer } = useCustomer();
  const path = usePathname();
  const accountType = useCheckoutStore((state) => state.accountType);

  if (!customer) {
    return (
      <section className="animate-pulse space-y-5">
        <div className="flex items-center justify-between">
          <div className="h-8 w-48 rounded bg-gray-200" />
          <div className="h-6 w-16 rounded bg-gray-200" />
        </div>
        <dl className="grid gap-y-10 md:grid-cols-2 md:gap-y-12.5">
          <div className="space-y-4">
            <div className="h-6 w-3/4 rounded bg-gray-200" />
            <div className="h-6 w-2/3 rounded bg-gray-200" />
            <div className="h-6 w-1/2 rounded bg-gray-200" />
          </div>
          <div className="space-y-4">
            <div className="h-6 w-3/4 rounded bg-gray-200" />
            <div className="h-6 w-2/3 rounded bg-gray-200" />
          </div>
        </dl>
      </section>
    );
  }

  const { first_name, last_name, email, phone, company_name } = customer;
  const titleLabel = getTitleLabel(customer.metadata?.title as string | undefined, locale);
  const birthDate = customer.metadata?.birth_date as string | undefined;
  const formattedBirthDate = convertDate(birthDate, "dd MMM yyyy", locale);

  return (
    <section className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="font-cabinet-grotesk text-2xl font-medium md:text-3xl">{__("My information", locale)}</h2>
        {path !== `/${locale}/account/edit` && (
          <Link
            href={`${getPermalink(locale, "account", "edit")}`}
            className="focus-visible:ring-dark-primary/70 cursor-pointer rounded-sm underline outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            {__("Edit", locale)}
          </Link>
        )}
      </div>

      <dl className="grid gap-y-10 md:grid-cols-2 md:gap-y-12.5">
        <div className="space-y-2.5">
          <div className="flex gap-1.5">
            <dt>{__("Civility", locale)} : </dt>
            <dd className="font-semibold">{titleLabel || "-"}</dd>
          </div>
          <div className="flex gap-1.5">
            <dt>{__("First name", locale)} : </dt>
            <dd className="font-semibold">{first_name || "-"}</dd>
          </div>
          <div className="flex gap-1.5">
            <dt>{__("Email", locale)} : </dt>
            <dd className="font-semibold">{email || "-"}</dd>
          </div>
          <div className="flex gap-1.5">
            <dt>{__("Telephone", locale)} : </dt>
            <dd className="font-semibold">{phone || "-"}</dd>
          </div>
        </div>

        <div className="space-y-2.5">
          <div className="flex gap-1.5">
            <dt>{__("Last name", locale)} : </dt>
            <dd className="font-semibold">{last_name || "-"}</dd>
          </div>
          <div className="flex gap-1.5">
            <dt>{__("Date of birth", locale)} : </dt>
            <dd className="font-semibold">{formattedBirthDate || "-"}</dd>
          </div>
          {company_name && accountType === "company" && (
            <div className="flex gap-1.5">
              <dt>{__("Company", locale)} : </dt>
              <dd className={cn("text-primary-light", company_name && "text-dark-primary font-semibold")}>
                {company_name || __("Not registered", locale)}
              </dd>
            </div>
          )}
        </div>
      </dl>
    </section>
  );
};

export default MyInformation;
